import { inject, Injectable } from '@angular/core';
import { Quote } from './model/quote';
import { AuthService, BrewersUnitOfWork } from './core.module';
import { QuoteStatus } from './model/quote-status';
import { Load } from './model/load';
import moment from 'moment';
import { TicketAttachment } from './model/ticket-attachment';
import { LoadService } from './load.service';
import { Response } from './model/response';
import _ from 'lodash';

@Injectable({
    providedIn: 'root',
})
export class QuoteService {
    uow = inject(BrewersUnitOfWork);
    auth = inject(AuthService);
    loadService = inject(LoadService);

    async createQuoteFromLoad(loadId: number): Promise<Response<Quote>> {
        const load = (await this.uow.loads.byId(loadId, 'customer.taxCode, product.category, loadAdditives.product')) as Load;

        if (!load.customer) return { success: false, message: 'Load must have a customer' };
        if (!load.product) return { success: false, message: 'Load must have a product' };

        const quote = this.uow.quotes.createEntity({
            statusId: QuoteStatus.Statuses.Pending,
            customerId: load.customerId,
            quoteDate: new Date(),
            expirationDate: moment(new Date()).add(30, 'days'),
            quoteCode: crypto.randomUUID(),
            created: new Date(),
            createdBy: this.auth.currentUser.userName,

            billToAddress1: load.customer.billAddress1,
            billToAddress2: load.customer.billAddress2,
            billToCity: load.customer.billCity,
            billToState: load.customer.billState,
            billToZip: load.customer.billZip,
            billToContactName: load.customer.contactName,
            billToCountry: load.customer.billCountry,
            billToEmail: load.customer.email,
            billToPhone: load.customer.phone,
            terms: load.customer.terms,

            projectName: load.description1,
            projectLocation: load.description2,
        });

        this.uow.quoteLines.createEntity({
            quoteId: quote.id,
            lineNumber: 1,
            productId: load.product.id,
            partNumber: load.product.partNumber,
            description: load.product.description,
            unitOfMeasureId: load.product.inventoryUomId,
            unit: load.unitOfMeasure,
            quantity: load.weightQuantity,
            price: load.price,
            isTaxable: this.loadService.isTaxable(load),
            created: new Date(),
            createdBy: this.auth.currentUser.userName,
        });

        let i = 1;
        if (!!load.loadAdditives && load.loadAdditives.length !== 0) {
            load.loadAdditives.forEach((additive) => {
                if (!!additive.product) {
                    this.uow.quoteLines.createEntity({
                        quoteId: quote.id,
                        lineNumber: i++,
                        productId: additive.product.id,
                        partNumber: additive.product.partNumber,
                        description: additive.product.description,
                        unitOfMeasureId: additive.product.inventoryUomId,
                        unit: null,
                        quantity: 0,
                        price: additive.price,
                        isTaxable: this.loadService.isTaxable(load),
                        created: new Date(),
                        createdBy: this.auth.currentUser.userName,
                    });
                }
            });
        }

        return { success: true, value: quote };
    }
}
